/* eslint-disable max-len */
import { useMemo, useState } from "react";
import {
  Show,
  SimpleShowLayout,
  useTranslate,
  FunctionField,
  useGetList,
  useShowContext
} from "react-admin";
import { Avatar, IconButton } from "@mui/material";
import ShowAndEditDetails from "./ShowAndEditDetails";
import {
  CancelledOrderIcon,
  EditButtonIcon,
  FailedOrderIcon,
  FullyPaidOrderIcon,
  OrderAmountIcon,
  OrderDetailsIcon,
  PaidUpfrontOrderIcon,
  PartialPaymentOrderIcon,
  PayInStoreOrderIcon,
  RefundedOrderIcon,
  TransactionsIcon
} from "assets/Icons";
import CustomHeaderActions from "./CustomHeaderActions";
import { muiColors } from "themes/colors";
import { Link, useLocation, useParams } from "react-router-dom";
import dayjs from "dayjs";
import OrderDetailsCustomButtons from "./OrderDetailsCustomButtons";
import OrderServicesAndItemsList from "./OrderServicesAndItemsList";
import OrderTransactionsList from "./OrderTransactionsList";
import OrderConfirmationModal from "./OrderConfirmationModal";
import OrderModal from "./OrderModal";
import AddToOrderModal from "./AddToOrderModal";
import CancellationModal from "./CancellationModal";
import AddArbitaryCreditsModal from "./AddArbitaryCreditsModal";
import { Elements } from "@stripe/react-stripe-js";
import { stripePromise } from "stripeConfig";
import { formatCurrency, getZonedDate } from "../utils/helpers";
import OrderEdit from "./OrderEdit";
import AddUserToOrderModal from "./AddUserToOrderModal";

const paymentStatus = {
  PAID_UPFRONT: <PaidUpfrontOrderIcon />,
  PAY_IN_STORE: <PayInStoreOrderIcon />,
  PAYMENT_FAILED: <FailedOrderIcon />,
  PARTIAL_PAYMENT: <PartialPaymentOrderIcon />,
  REFUNDED: <RefundedOrderIcon />,
  CANCELLED: <CancelledOrderIcon />,
  FULLY_PAID: <FullyPaidOrderIcon />
};

export default function OrderShow() {
  const translate = useTranslate();
  const [cancelModalAppointmentId, setCancelModalAppointmentId] = useState<
    string | null
  >(null);
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(
    location?.state?.checkout ? true : false
  );
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [whatToAdd, setWhatToAdd] = useState<
    "SERVICE" | "ADDON" | "FREEFORM" | "DISCOUNT" | null
  >(null);
  const [isAddArbitaryCreditsModalOpen, setIsAddArbitaryCreditsModalOpen] =
    useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<
    string | null
  >(null);
  const [addUserToOrder, setAddUserToOrder] = useState<
    { id: string | undefined; charge: boolean } | undefined
  >(undefined);

  const { data, isLoading, refetch } = useGetList("TxService", {
    filter: { order_id: id },
    pagination: { page: 1, perPage: 100 }
  });

  const {
    data: txAddOns,
    isLoading: isLoadingTxAddon,
    refetch: txAddonRefetch
  } = useGetList("TxAddon", {
    filter: { order_id: id },
    pagination: { page: 1, perPage: 100 }
  });

  const { data: txItemData, refetch: txItemRefetch } = useGetList("TxItem", {
    filter: { order_id: id },
    pagination: { page: 1, perPage: 100 }
  });

  const combinedDataWithQuantity = useMemo(
    () => [
      ...(data || []).map((item) => ({
        ...item,
        type: "Service",
        quantity: 1
      })),
      ...(txItemData || []).map((item) => ({
        ...item,
        type: item.price < 0 ? "Discount" : "Freeform",
        quantity: 1
      })),
      ...(txAddOns || []).map((item) => ({
        ...item,
        type: "Addon",
        quantity: 1
      }))
    ],
    [data, txItemData, txAddOns]
  );

  const { combinedNewData, totalPrice, totalDiscount } = useMemo(() => {
    let totalPrice = 0;
    let totalDiscount = 0;

    const combinedNewData = combinedDataWithQuantity?.reduce((acc, e) => {
      if (e.type !== "Discount") {
        const found = acc?.find(
          (x: any) =>
            (e?.service_id === x?.service_id && e?.type === "Service") ||
            (e?.addon_id === x?.addon_id && e?.type === "Addon")
        );
        found ? (found.quantity += e?.quantity) : acc?.push(e);
      } else {
        totalDiscount += Math.abs(e?.price);
      }
      return acc;
    }, []);

    combinedNewData?.forEach((item: any) => {
      if (item.type !== "Discount") {
        totalPrice += (item.quantity || 1) * item.price;
      }
    });

    return { combinedNewData, totalPrice, totalDiscount };
  }, [combinedDataWithQuantity]);

  return (
    <Show
      actions={<CustomHeaderActions title={translate("pagenames.order")} />}
      sx={{
        "& .RaShow-card": {
          backgroundColor: muiColors.bg.default
        },
        "& .RaDatagrid-headerCell": {
          borderTop: "1px solid rgba(224, 224, 224, 1)",
          paddingTop: "16px",
          paddingBottom: "16px",
          lineHeight: "18px"
        },
        "& .RaDatagrid-rowCell": {
          borderBottom: "none"
        }
      }}
    >
      <div className="w-full">
        <div className="h-min w bg-mono-primaryLightBgColor rounded-[8px] mb-4">
          <SimpleShowLayout>
            <ShowAndEditDetails
              title={translate("order.order_details")}
              view="show"
              icon={<OrderDetailsIcon />}
              hasEdit={false}
              customButtons={
                <OrderDetailsCustomButtons
                  openChargeModal={() => setIsOpen(true)}
                  setWhatToAdd={setWhatToAdd}
                  setIsConfirmationModalOpen={setIsConfirmationModalOpen}
                  setCancelModalAppointmentId={setCancelModalAppointmentId}
                  openAddArbitaryCreditsModal={() =>
                    setIsAddArbitaryCreditsModalOpen(true)
                  }
                  openEditModal={
                    combinedDataWithQuantity?.length > 0
                      ? () => setIsEditOpen(true)
                      : null
                  }
                  openAddUserModal={() =>
                    setAddUserToOrder({ id: id, charge: true })
                  }
                />
              }
              marginBottom="-34px"
            />
            <Elements stripe={stripePromise}>
              <div style={{ marginTop: isOpen ? "0px" : "31px" }}>
                {isOpen && <OrderModal isOpen={isOpen} setIsOpen={setIsOpen} />}
              </div>
            </Elements>
            {whatToAdd && (
              <AddToOrderModal
                whatToAdd={whatToAdd}
                setWhatToAdd={setWhatToAdd}
                refetch={refetch}
                txItemRefetch={txItemRefetch}
                txAddonRefetch={txAddonRefetch}
              />
            )}
            {isAddArbitaryCreditsModalOpen && (
              <AddArbitaryCreditsModal
                isOpen={isAddArbitaryCreditsModalOpen}
                setIsOpen={setIsAddArbitaryCreditsModalOpen}
                refetch={refetch}
                txItemRefetch={txItemRefetch}
                txAddonRefetch={txAddonRefetch}
              />
            )}
            <OrderInfo
              id={id}
              openAddUserModal={() =>
                setAddUserToOrder({ id: id, charge: false })
              }
            />
            <FunctionField
              label={translate("appointment.location") + ":"}
              render={(record: any) => (
                <div className="flex">
                  <Link
                    to={`/dashboard/Location/${
                      record?.appointment?.location?.id || record?.location?.id
                    }/show`}
                  >
                    <div className="text-[16px] text-[#16667A] font-[400] font-['Inter']">
                      {record?.appointment?.location?.nickname ||
                        record?.location?.nickname ||
                        record?.appointment?.location?.name ||
                        record?.location?.name}
                    </div>
                  </Link>
                </div>
              )}
            />
            <FunctionField
              label={translate("order.appointment") + ":"}
              render={(record: any) => {
                if (record?.appointment) {
                  return (
                    <div className="flex">
                      <Link
                        data-cy="appointment-redirect-button"
                        to={`/dashboard/Appointment/${record.appointment.id}/show`}
                      >
                        <div className="text-[16px] text-[#16667A] font-[400] font-['Inter']">
                          {dayjs(
                            getZonedDate(
                              record.appointment.start_time,
                              record.appointment.location?.timezone
                            )
                          ).format("hh:mm A")}{" "}
                          -{" "}
                          {dayjs(
                            getZonedDate(
                              record.appointment.end_time,
                              record.appointment.location?.timezone
                            )
                          ).format("hh:mm A")}
                          ,{" "}
                          {dayjs(
                            getZonedDate(
                              new Date().toISOString(),
                              record.appointment.location?.timezone
                            )
                          ).isSame(
                            dayjs(
                              getZonedDate(
                                record.appointment.start_time,
                                record.appointment.location?.timezone
                              )
                            ).format("MM/DD/YYYY"),
                            "day"
                          )
                            ? "Today"
                            : dayjs(
                                getZonedDate(
                                  record.appointment.start_time,
                                  record.appointment.location?.timezone
                                )
                              ).format("MM/DD/YYYY")}
                        </div>
                      </Link>
                    </div>
                  );
                } else {
                  return <span>-</span>;
                }
              }}
            />
            <OrderServicesAndItemsList
              data={combinedNewData}
              isLoading={isLoading || isLoadingTxAddon}
              price={totalPrice}
              totalDiscount={totalDiscount}
            />
          </SimpleShowLayout>
        </div>
        <div className="h-min w-full bg-mono-primaryLightBgColor rounded-[8px] py-2 px-4">
          <ShowAndEditDetails
            title={translate("order.transactions")}
            view="show"
            icon={<TransactionsIcon />}
            hasEdit={false}
            marginBottom="0px"
            borderBottom="none"
          />
          <OrderTransactionsList deleteEnabled={true} />
        </div>
      </div>
      <OrderConfirmationModal
        isConfirmationModalOpen={isConfirmationModalOpen}
        setIsConfirmationModalOpen={setIsConfirmationModalOpen}
      />
      {isEditOpen && (
        <OrderEdit
          orderId={id}
          data={combinedDataWithQuantity}
          isEditOpen={isEditOpen}
          onClose={() => setIsEditOpen(false)}
        />
      )}
      {cancelModalAppointmentId && (
        <CancellationModal
          cancelModalAppointmentId={cancelModalAppointmentId}
          setCancelModalAppointmentId={setCancelModalAppointmentId}
        />
      )}
      {addUserToOrder?.id && (
        <AddUserToOrderModal
          open={!!addUserToOrder?.id}
          onClose={() => setAddUserToOrder(undefined)}
          addUserToOrder={addUserToOrder}
          openChargeModal={() => setIsOpen(true)}
        />
      )}
    </Show>
  );
}

const OrderInfo = ({
  id,
  openAddUserModal
}: {
  id: string | undefined;
  openAddUserModal: () => void;
}) => {
  const translate = useTranslate();
  const { record } = useShowContext();
  const { data: transactions } = useGetList("Transaction", {
    filter: {
      order_id: id,
      status: "SUCCESS",
      // Exclude real Stripe refunds (downloaded separately as refundTransactions),
      // but keep merch-credit refunds (type REFUND + payment_method REFUND).
      NOT: [{ type: "REFUND", payment_method: "STRIPE" }]
    },
    sort: { field: "id", order: "ASC" },
    pagination: { page: 1, perPage: 100 }
  });
  const { data: refundTransactions } = useGetList("Transaction", {
    filter: {
      order_id: id,
      status: "SUCCESS",
      type: "REFUND",
      payment_method: "STRIPE"
    },
    sort: { field: "id", order: "ASC" },
    pagination: { page: 1, perPage: 100 }
  });

  const amountPaidByUserNotIncludingTips = transactions?.reduce(
    (acc: number, curr: any) => {
      acc += curr?.amount ?? 0;
      acc -= curr?.payment_fees ?? 0;
      acc -= curr?.total_tip_amount ?? 0;
      return acc;
    },
    0
  );

  const totalTipsCaptured = transactions?.reduce((acc: number, curr: any) => {
    acc += curr?.total_tip_amount ?? 0;
    return acc;
  }, 0);
  const totalRefundCaptured = refundTransactions?.reduce(
    (acc: number, curr: any) => {
      acc += curr?.amount ?? 0;
      return acc;
    },
    0
  );
  return (
    <div className="flex flex-col sm:flex-row w-fit gap-5 sm:gap-8 flex-wrap">
      <div className="flex-1 min-w-[180px] max-w-fit">
        <Avatar
          sx={{
            bgcolor: "rgba(238, 234, 228, 0.9)",
            color: "rgba(48, 47, 46, 0.95)",
            width: "40px",
            height: "40px",
            fontFamily: "Inter",
            fontSize: "18px",
            marginBottom: "16px",
            "@media screen and (max-width:640px)": {
              marginBottom: "6px"
            }
          }}
          alt={record?.user?.first_name}
          src={record?.user?.photo_url || "monogram"}
        />
        <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
          {translate("order.user")}:
        </div>
        {record?.user?.first_name && (
          <div className="flex gap-2 items-center pr-8">
            <Link
              data-cy="user-redirect-button"
              to={`/dashboard/Customer/${record?.user?.id}/show`}
            >
              <div className="line-clamp-2 break-all text-[24px] text-[#16667A] font-[600] leading-[133%] font-['OptimaLTStd'] capitalize">
                {record?.user?.first_name + " " + record?.user?.last_name}
              </div>
            </Link>
            {!record?.appointment && (
              <IconButton onClick={openAddUserModal}>
                <EditButtonIcon />
              </IconButton>
            )}
          </div>
        )}
        {!record?.user?.first_name && (
          <div className="line-clamp-2 break-all text-[24px] text-mono-primaryTextColor font-[600] leading-[133%] pr-8 font-['OptimaLTStd']">
            {translate("order.walk_in")}{" "}
            <IconButton onClick={openAddUserModal}>
              <EditButtonIcon />
            </IconButton>
          </div>
        )}
      </div>
      <div className="flex-1 min-w-[180px] max-w-fit">
        <div className="mb-1.5 sm:mb-4">
          {record?.payment_status &&
            paymentStatus[record?.payment_status as keyof typeof paymentStatus]}
        </div>
        <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
          {translate("order.status")}:
        </div>
        <div className="text-[24px] text-mono-primaryTextColor font-[600] leading-[133%] pr-8 font-['OptimaLTStd'] first-letter:capitalize">
          {record?.payment_status?.replaceAll("_", " ")?.toLowerCase()}
        </div>
      </div>
      <div className="flex-1 min-w-[180px] max-w-fit">
        <div className="mb-1.5 sm:mb-4">
          <OrderAmountIcon />
        </div>
        <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
          {translate("order.amount_captured")}:
        </div>
        <div className="text-[24px] text-mono-primaryTextColor font-[600] leading-[133%] pr-8 font-['OptimaLTStd']">
          {formatCurrency(amountPaidByUserNotIncludingTips)} of{" "}
          {formatCurrency(record?.full_amount)}
        </div>
      </div>
      <div className="flex-1 min-w-[180px] max-w-fit">
        <div className="mb-1.5 sm:mb-4">
          <OrderAmountIcon />
        </div>
        <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
          {translate("order.tips_captured")}:
        </div>
        <div className="text-[24px] text-mono-primaryTextColor font-[600] leading-[133%] pr-8 font-['OptimaLTStd']">
          {formatCurrency(totalTipsCaptured)}
        </div>
      </div>
      {!!totalRefundCaptured && (
        <div className="flex-1 min-w-[180px] max-w-fit">
          <div className="mb-1.5 sm:mb-4">
            <OrderAmountIcon />
          </div>
          <div className="text-[14px] text-[rgba(0,0,0,0.6)] font-['Inter']">
            {translate("order.refunded")}:
          </div>
          <div className="text-[24px] text-mono-primaryTextColor font-[600] leading-[133%] pr-8 font-['OptimaLTStd']">
            {formatCurrency(totalRefundCaptured)}
          </div>
        </div>
      )}
    </div>
  );
};
